import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Inbox, ListChecks, Bell, MessageSquare, Clock,
  AlertTriangle, CheckCircle2, ArrowRight, Plus, UserPlus, Briefcase,
} from "lucide-react";

type Scope = "mine" | "all" | "user";

type ViewState = { scope: Scope; userId?: number };

function storageKey(userId: number | undefined): string {
  return `home-dashboard-view-${userId ?? "anon"}`;
}

function loadView(userId: number | undefined, defaultScope: Scope): ViewState {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.scope === "mine" || parsed.scope === "all" || parsed.scope === "user")) {
        return parsed;
      }
    }
  } catch { /* ignore */ }
  return { scope: defaultScope };
}

function saveView(userId: number | undefined, view: ViewState) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(view));
  } catch { /* ignore */ }
}

function StatCard({ title, value, sub, icon: Icon, color, onClick }: {
  title: string; value: number | string; sub?: string; icon: any; color: string; onClick?: () => void;
}) {
  return (
    <Card
      className={onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}
      onClick={onClick}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="p-2 rounded-lg bg-muted/50">
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function relativeTime(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = new Date(date);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin}min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `há ${diffD}d`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function reminderRelativeLabel(date: string | Date): string {
  const d = new Date(date);
  const diffMs = d.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 0) {
    const overdue = Math.abs(diffMin);
    if (overdue < 60) return `atrasado ${overdue}min`;
    const overdueH = Math.round(overdue / 60);
    if (overdueH < 24) return `atrasado ${overdueH}h`;
    return `atrasado ${Math.round(overdueH / 24)}d`;
  }
  if (diffMin < 60) return `em ${diffMin}min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `em ${diffH}h`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

export default function HomeDashboard() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [, setLocation] = useLocation();

  const userId = (user as any)?.id as number | undefined;
  const userName = (user as any)?.name as string | undefined;

  const canSeeAllInbox = can("inbox:view_all");
  const canSeeAllCrm = can("crm:deals_view_all");
  const canSeeTeam = canSeeAllInbox || canSeeAllCrm;
  const canViewTeammates = can("users:view");
  const canManageReminders = can("inbox:manage_reminders");
  const canViewTasks = can("crm:tasks_view");

  const defaultScope: Scope = canSeeTeam ? "all" : "mine";
  const [view, setView] = useState<ViewState>(() => loadView(userId, defaultScope));

  // Sync scope with localStorage and re-evaluate when user changes
  useEffect(() => {
    if (userId !== undefined) {
      saveView(userId, view);
    }
  }, [userId, view]);

  // Force "mine" if user lost permission to see team
  useEffect(() => {
    if (!canSeeTeam && view.scope !== "mine") {
      setView({ scope: "mine" });
    }
  }, [canSeeTeam, view.scope]);

  const queryInput = useMemo(() => {
    if (view.scope === "user" && view.userId) {
      return { scope: "user" as const, userId: view.userId };
    }
    return { scope: view.scope as "mine" | "all" };
  }, [view]);

  const { data: summary } = trpc.dashboard.homeSummary.useQuery(queryInput);
  const { data: openConversations = [], isLoading: loadingConversations } = trpc.inbox.myOpenConversations.useQuery({
    ...queryInput,
    limit: 8,
  });
  const { data: upcomingReminders = [] } = trpc.inbox.myUpcomingReminders.useQuery(
    { ...queryInput, limit: 5 },
    { enabled: canManageReminders },
  );
  const { data: tasksToday = [] } = trpc.crm.tasks.list.useQuery(
    { scope: view.scope === "mine" ? "today" : "today", ownerUserId: view.scope === "user" ? view.userId : undefined, limit: 5 },
    { enabled: canViewTasks },
  );
  const { data: tasksOverdue = [] } = trpc.crm.tasks.list.useQuery(
    { scope: "overdue", ownerUserId: view.scope === "user" ? view.userId : undefined, limit: 5 },
    { enabled: canViewTasks },
  );
  // Lista de colegas só carrega se o usuário pode ver "users" e tem perm de visão ampliada
  const { data: usersData } = trpc.users.list.useQuery(undefined, {
    enabled: canSeeTeam && canViewTeammates,
  });
  const teammates: any[] = Array.isArray(usersData) ? usersData : [];

  // ─── UI ────────────────────────────────────────────────────────────────────
  const utils = trpc.useUtils();
  const completeTask = trpc.crm.tasks.complete.useMutation({
    onSuccess: () => {
      utils.crm.tasks.list.invalidate();
      utils.dashboard.homeSummary.invalidate();
    },
  });

  const scopeLabel = view.scope === "all"
    ? "Toda a equipe"
    : view.scope === "user"
      ? (teammates.find((u: any) => u.id === view.userId)?.name ?? "Outro usuário")
      : "Eu mesmo";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold">
              {greeting()}{userName ? `, ${userName.split(" ")[0]}` : ""}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Veja o que precisa da sua atenção hoje.
            </p>
          </div>

          {canSeeTeam && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Visão</span>
              <Select
                value={view.scope === "user" ? `user:${view.userId}` : view.scope}
                onValueChange={(v) => {
                  if (v === "all") setView({ scope: "all" });
                  else if (v === "mine") setView({ scope: "mine" });
                  else if (v.startsWith("user:")) {
                    const id = Number(v.slice(5));
                    if (!Number.isNaN(id)) setView({ scope: "user", userId: id });
                  }
                }}
              >
                <SelectTrigger className="h-9 w-[200px]">
                  <SelectValue placeholder="Visão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toda a equipe</SelectItem>
                  <SelectItem value="mine">Eu mesmo</SelectItem>
                  {canViewTeammates && teammates.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Por usuário</div>
                      {teammates
                        .filter((u: any) => u.id !== userId)
                        .map((u: any) => (
                          <SelectItem key={u.id} value={`user:${u.id}`}>{u.name}</SelectItem>
                        ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* ── KPIs ────────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Atendimentos abertos"
            value={summary?.openConversations ?? 0}
            sub={scopeLabel}
            icon={MessageSquare}
            color="text-blue-600"
            onClick={() => setLocation("/inbox")}
          />
          {canSeeTeam && (
            <StatCard
              title="Sem agente"
              value={summary?.unassignedConversations ?? 0}
              sub="aguardando atribuição"
              icon={Clock}
              color="text-amber-600"
              onClick={() => setLocation("/inbox")}
            />
          )}
          {canViewTasks && (
            <>
              <StatCard
                title="Tarefas hoje"
                value={summary?.tasksToday ?? 0}
                sub="vencem hoje"
                icon={ListChecks}
                color="text-violet-600"
                onClick={() => setLocation("/crm/tasks")}
              />
              <StatCard
                title="Tarefas atrasadas"
                value={summary?.tasksOverdue ?? 0}
                sub="vencidas e abertas"
                icon={AlertTriangle}
                color="text-red-600"
                onClick={() => setLocation("/crm/tasks")}
              />
            </>
          )}
          {canManageReminders && (
            <StatCard
              title="Lembretes (7d)"
              value={summary?.remindersUpcoming ?? 0}
              sub="próximos 7 dias"
              icon={Bell}
              color="text-emerald-600"
            />
          )}
        </div>

        {/* ── Seções principais ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Atendimentos abertos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-primary" />
                  Atendimentos abertos
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setLocation("/inbox")}
                >
                  Ver todos <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingConversations ? (
                <p className="text-sm text-muted-foreground p-6 text-center">Carregando…</p>
              ) : openConversations.length === 0 ? (
                <p className="text-sm text-muted-foreground p-6 text-center">
                  Nenhum atendimento aberto {view.scope === "mine" ? "para você" : "no momento"}.
                </p>
              ) : (
                <ul className="divide-y">
                  {(openConversations as any[]).map((c) => (
                    <li
                      key={c.id}
                      className="px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => setLocation("/inbox")}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">
                              {c.contactName || c.contactPhone || "Contato"}
                            </p>
                            {c.unreadCount > 0 && (
                              <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-100">
                                {c.unreadCount}
                              </Badge>
                            )}
                            {!c.assignedTo && (
                              <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">
                                sem agente
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {c.lastMessageContent || "—"}
                          </p>
                        </div>
                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                          {relativeTime(c.lastMessageAt)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Tarefas do CRM */}
          {canViewTasks && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-primary" />
                    Tarefas do CRM
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setLocation("/crm/tasks")}
                  >
                    Ver todas <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {tasksToday.length === 0 && tasksOverdue.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-6 text-center">Nenhuma tarefa pendente.</p>
                ) : (
                  <ul className="divide-y">
                    {[...tasksOverdue, ...tasksToday].slice(0, 6).map((row: any) => {
                      const t = row.task ?? row;
                      const overdue = t.dueAt && new Date(t.dueAt) < new Date();
                      return (
                        <li key={t.id} className="px-4 py-3 hover:bg-muted/30 transition-colors">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{t.title}</p>
                              <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
                                {row.owner?.name && <span>{row.owner.name}</span>}
                                {row.deal?.title && (
                                  <>
                                    <span>·</span>
                                    <span className="truncate">{row.deal.title}</span>
                                  </>
                                )}
                                {t.dueAt && (
                                  <>
                                    <span>·</span>
                                    <span className={overdue ? "text-red-600 font-medium" : ""}>
                                      {overdue ? "atrasada" : reminderRelativeLabel(t.dueAt)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => completeTask.mutate({ id: t.id })}
                              disabled={completeTask.isPending}
                              title="Marcar como concluída"
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── Lembretes (opcional) ────────────────────────────────────────── */}
        {canManageReminders && upcomingReminders.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                Lembretes próximos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y">
                {(upcomingReminders as any[]).map((r) => (
                  <li
                    key={r.id}
                    className="px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => setLocation("/inbox")}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">
                          {r.contactName || r.contactPhone || "Conversa"}
                        </p>
                        {r.note && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{r.note}</p>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {reminderRelativeLabel(r.remindAt)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* ── Atalhos rápidos ────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ações rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {can("inbox:view") && (
                <Button variant="outline" className="justify-start h-10" onClick={() => setLocation("/inbox")}>
                  <Inbox className="h-4 w-4 mr-2" /> Atendimento
                </Button>
              )}
              {can("crm:tasks_manage") && (
                <Button variant="outline" className="justify-start h-10" onClick={() => setLocation("/crm/tasks")}>
                  <Plus className="h-4 w-4 mr-2" /> Nova tarefa
                </Button>
              )}
              {can("contacts:create") && (
                <Button variant="outline" className="justify-start h-10" onClick={() => setLocation("/contacts")}>
                  <UserPlus className="h-4 w-4 mr-2" /> Novo contato
                </Button>
              )}
              {can("crm:deals_create") && (
                <Button variant="outline" className="justify-start h-10" onClick={() => setLocation("/crm/deals")}>
                  <Briefcase className="h-4 w-4 mr-2" /> Nova oportunidade
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
